import { CircularProgress, Stack, Typography } from '@mui/material';
import { ProductTenor, ProductType } from '@protos/product';
import { Side } from '@protos/trading';
import { TradeLimit } from '@protos/user';
import { OtcOrderType } from '@protos/v2/otcQuote';
import { streamV2RFQ } from '@services/context';
import { toastifyService } from '@services/ToastifyService';
import { useDashboardContext } from '@shared/contexts/DashboardContext';
import { useTradableProductSymbolsContext } from '@shared/contexts/TradableProductSymbolsProvider';
import { useTradingTenorContext } from '@shared/contexts/TradingTenorProvider';
import { useUserProductsAndTenorsContext } from '@shared/contexts/UserProductsAndTenorsProvider';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTradingForm } from '../hooks/useTradingForm';
import { TradingPanel, useTradingContext } from '../TradingContext';
import { TradeConfirmation } from './TradeConfirmation';
import { TradeSizeInput } from './TradeSizeInput';
import { TradingButtons } from './TradingButtons';
import { TradingFormControls } from './TradingFormControls';
import { TradingPanelPriceEntry } from './TradingPanelPriceEntry';
import { isAfterSettlementTime } from '@shared/utils/date';

interface TradingPanelProps {
  id: string;
  payload: TradingPanel;
  userTradeLimits: TradeLimit[];
  defaultTradeSizes: Record<string, number>;
}

export const TradingPanelComponent = ({ id, payload, userTradeLimits, defaultTradeSizes }: TradingPanelProps) => {
  const sizeInputRef = useRef<HTMLDivElement>(null);
  const { editWidgetPayloadById } = useDashboardContext();
  const { tenors } = useUserProductsAndTenorsContext();
  const {
    subscriptionSymbol,
    subscriptionSize,
    subscriptionExchange,
    setSubscriptionSymbol,
    setSubscriptionSize,
    setSubscriptionExchange,
    setPrices,
    unsubscribeSymbol,
  } = useTradingContext();
  const { getAllUsedTenors, setWidgetTenorMap, widgetTenorMap } = useTradingTenorContext();

  const currentWidgetTenorMap = useMemo(() => widgetTenorMap[id], [widgetTenorMap, id]);

  const {
    tradingForm,
    rawTradingAmount,
    isLoadingPanel: tradingFormLoadingPanel,
    tenorsNeedUpdate: tradingTenorsLoading,
    isTradeLimitBreached: tradingFormTradeLimitBreached,
    showUserInputError,
    allowedTradingProducts: tradingFormAllowedTradingProducts,
    availableTradingTenors,
    validTenors,
    validBackTenors,
    onTradingProductChange,
    onTenorChange,
    onExchangeChange,
    onTradeSizeChange,
    onOrderTypeChange,
    setIsLoadingPanel,
    setValidTenors,
    setValidBackTenors,
    getAvailableRightTenors,
    onToggleBuyLock,
    onToggleSellLock,
    isBelowMinTrade,
    minTrade,
    isValidExchange,
    isQuantityUnitSpecified,
  } = useTradingForm({ id, payload, userTradeLimits, tenors, defaultTradeSizes });
  const { allProducts } = useTradableProductSymbolsContext();
  const { prices } = useTradingContext();
  const [tradeConfirmationVisible, setTradeConfirmationVisible] = useState(false);
  const [confirmationSide, setConfirmationSide] = useState<Side | null>(null);
  const [isUpdatingSize, setIsUpdatingSize] = useState(false);
  const formProduct = allProducts.find(product => product.symbol.toLowerCase() === tradingForm.selectedProduct?.toLowerCase());
  const isMocProduct = formProduct?.product_type === ProductType.MOC;

  const [afterSettlementTime, setAfterSettlementTime] = useState<boolean>(() => isAfterSettlementTime(formProduct, tradingForm.selectedExchange));
  const disableTradeButtons = isUpdatingSize || showUserInputError || afterSettlementTime;
  const isMocAfterSettlementTime = isMocProduct && afterSettlementTime;

  const handleTradeSizeUpdating = (isUpdating: boolean) => {
    setIsUpdatingSize(isUpdating);
  };

  const handleSettlementTimeCheck = () => {
    setAfterSettlementTime(isAfterSettlementTime(formProduct, tradingForm.selectedExchange));
  };

  // State for limit
  const [limitBuyPrice, setLimitBuyPrice] = useState(0);
  const [limitSellPrice, setLimitSellPrice] = useState(0);
  const [limitTradeState, setLimitTradeState] = useState(Side.UNSPECIFIED);
  const [tickSize, setTickSize] = useState(Math.pow(10, -2));

  useEffect(() => {
    setLimitTradeState(Side.UNSPECIFIED);
    setLimitBuyPrice(0);
    setLimitSellPrice(0);
  }, [tradingForm.selectedProduct, tradingForm.selectedTenor, tradingForm.selectedExchange, tradingForm.selectedOrderType, tradingForm.tradeSize]);

  const showTradeConfirmation = useCallback((otcSide: Side) => {
    setConfirmationSide(otcSide);
    setTradeConfirmationVisible(true);
  }, []);

  const onCloseTradeConfirmation = useCallback(() => {
    setTradeConfirmationVisible(false);
  }, []);

  const onPlaceTrade = (key: Side) => {
    if (isAfterSettlementTime(formProduct, tradingForm.selectedExchange)) {
      toastifyService.showErrorMessage('Cannot place trade after settlement time');
      return;
    }
    onCloseTradeConfirmation();
    if (!subscriptionSymbol) return;

    const numDecimals = Math.abs(Math.log10(tickSize));

    const [front, back] = subscriptionSymbol.split('-');
    const isSpreadSymbol = subscriptionSymbol.split('-').length === 2;
    const tradeSellPrice = prices.sell?.price.toString();
    const tradeBuyPrice = prices.buy?.price.toString();
    const orderType = tradingForm.selectedOrderType;
    const tradePrice = key === Side.BUY ? tradeBuyPrice : tradeSellPrice;
    if (!tradePrice) return;

    let price = tradePrice;
    if (orderType === OtcOrderType.limit) {
      price = key === Side.BUY ? limitBuyPrice.toFixed(numDecimals) : limitSellPrice.toFixed(numDecimals);
    }

    streamV2RFQ.placeOrderQuote({
      symbol: isSpreadSymbol ? { front, back } : subscriptionSymbol,
      amount: subscriptionSize.toString(),
      side: key,
      price,
      exchange: subscriptionExchange,
      account_id: payload.selectedTradingAccount,
      order_type: tradingForm.selectedOrderType,
    });

    setLimitTradeState(Side.UNSPECIFIED);
  };

  //Highlight Size input field on widget load
  useEffect(() => {
    if (sizeInputRef.current) {
      sizeInputRef.current?.focus();
    }
  }, [sizeInputRef.current]);

  // Save widget based on user input in trading form and update widgetTenorMap
  useEffect(() => {
    const { selectedProduct, selectedTenor, tradeSize, selectedExchange, selectedTradingAccount } = tradingForm;
    if (!selectedProduct || !selectedTenor || !tradeSize || !selectedExchange || !selectedTradingAccount || tradingTenorsLoading) return;

    const product = allProducts.find(product => product.symbol.toLowerCase() === selectedProduct.toLowerCase());
    if (product === undefined) {
      return;
    }

    setTickSize(product.tick_size);

    // TODO: this isn't the actual logic that's supposed to be used
    // See utils.ts -> getRFQSymbolForSingleTenor / getRFQSymbolForSpread
    const rfqSubscriptionSymbol =
      typeof selectedTenor === 'string'
        ? `${selectedProduct}${selectedTenor}`
        : `${formProduct?.underlying_symbol}${selectedTenor.left}-${formProduct?.underlying_symbol}${selectedTenor.right}`;

    if (subscriptionSymbol === rfqSubscriptionSymbol && subscriptionSize === tradeSize && subscriptionExchange === selectedExchange) {
      setIsLoadingPanel(false);
      return;
    }

    const isSpread = typeof selectedTenor === 'object';
    const tenorsUsed = isSpread
      ? [
          {
            left: availableTradingTenors.find(tenor => tenor?.code === selectedTenor?.left),
            right: availableTradingTenors.find(tenor => tenor?.code === selectedTenor?.right),
          },
        ]
      : [availableTradingTenors.find(tenor => tenor?.code === selectedTenor)];
    const nonUndefinedTenorsUsed = tenorsUsed.filter(tenor => tenor !== undefined) as ProductTenor[];

    setWidgetTenorMap(prev => ({
      ...prev,
      [id]: { isSpread, exchange: selectedExchange, tenorsUsed: nonUndefinedTenorsUsed, product: selectedProduct },
    }));
    setPrices({ buy: undefined, sell: undefined });

    const currentSubscriptionSymbolElements = `${subscriptionSymbol}-${subscriptionExchange}`.split('-');

    if (currentSubscriptionSymbolElements.length !== `${rfqSubscriptionSymbol}-${selectedExchange}`.split('-').length) {
      unsubscribeSymbol();
    } else {
      if (currentSubscriptionSymbolElements.length === 3 && typeof selectedTenor !== 'string') {
        if (
          `${formProduct?.underlying_symbol}${selectedTenor.left}` !== currentSubscriptionSymbolElements[0] ||
          `${formProduct?.underlying_symbol}${selectedTenor.right}` !== currentSubscriptionSymbolElements[1] ||
          currentSubscriptionSymbolElements[2] !== selectedExchange
        )
          unsubscribeSymbol();
      } else if (
        `${selectedProduct}${selectedTenor}` !== currentSubscriptionSymbolElements[0] ||
        selectedExchange !== currentSubscriptionSymbolElements[1]
      ) {
        unsubscribeSymbol();
      }
    }

    if (subscriptionSymbol !== rfqSubscriptionSymbol) setSubscriptionSymbol(rfqSubscriptionSymbol);
    if (subscriptionSize !== tradeSize) setSubscriptionSize(tradeSize);
    if (subscriptionExchange !== selectedExchange) setSubscriptionExchange(selectedExchange);
    editWidgetPayloadById(id, {
      selectedProduct,
      selectedTenor,
      tradeSize,
      selectedTradingAccount,
      selectedExchange,
    });

    if (tradingFormLoadingPanel) setIsLoadingPanel(false);
  }, [
    tradingForm,
    id,
    subscriptionSymbol,
    setWidgetTenorMap,
    availableTradingTenors,
    tradingTenorsLoading,
    subscriptionSize,
    subscriptionExchange,
    formProduct,
  ]);

  const isTenorsFiltered = useMemo(() => validTenors.length !== availableTradingTenors.length, [validTenors.length, availableTradingTenors.length]);

  // Update Tenors based on WidgetTenorMap changes
  useEffect(() => {
    if (!currentWidgetTenorMap) return;

    if (currentWidgetTenorMap.isSpread) {
      const allUsedTenors = getAllUsedTenors(id, true, currentWidgetTenorMap.exchange, currentWidgetTenorMap.product) as {
        left: ProductTenor;
        right: ProductTenor;
      }[];
      const currentWidgetTenor = currentWidgetTenorMap.tenorsUsed[0] as { left: ProductTenor; right: ProductTenor };
      if (isTenorsFiltered) setValidTenors(availableTradingTenors);
      if (!currentWidgetTenor || !currentWidgetTenor.left || !currentWidgetTenor.right) return;

      const usedSpreadProductTenors = allUsedTenors.filter(t => t.left?.code === currentWidgetTenor.left?.code);
      const usedRightTenorCodes = usedSpreadProductTenors.map(t => t.right?.code);
      const availableRightTenors = getAvailableRightTenors(currentWidgetTenor.left?.code, usedRightTenorCodes);

      setValidBackTenors(availableRightTenors);
    } else {
      const allUsedTenors = getAllUsedTenors(id, false, currentWidgetTenorMap.exchange, currentWidgetTenorMap.product) as ProductTenor[];
      const availableNonSprTenors = availableTradingTenors.filter(tenor => !allUsedTenors.find(t => t?.code === tenor?.code));

      setValidTenors(availableNonSprTenors);
    }
  }, [currentWidgetTenorMap, id, availableTradingTenors, getAllUsedTenors, isTenorsFiltered]);

  // Remove widgetTenorMap when widget unmounts
  useEffect(() => {
    return () => {
      setWidgetTenorMap(prev => {
        const newWidgetTenorMap = { ...prev };
        delete newWidgetTenorMap[id];
        return newWidgetTenorMap;
      });
    };
  }, []);

  // Unsubscribe from subscription when widget unmounts
  useEffect(() => {
    return () => {
      if (subscriptionSymbol) {
        unsubscribeSymbol();
      }
    };
  }, [subscriptionSymbol, unsubscribeSymbol]);

  // Initialize or update the widgetTenorMap for this widget
  useEffect(() => {
    const { selectedProduct, selectedTenor, selectedExchange } = tradingForm;
    if (!selectedProduct || !selectedTenor || !selectedExchange) return;

    const isSpread = typeof selectedTenor === 'object';
    const tenorsUsed: any = isSpread
      ? [
          {
            left: availableTradingTenors.find(tenor => tenor?.code === selectedTenor?.left),
            right: availableTradingTenors.find(tenor => tenor?.code === selectedTenor?.right),
          },
        ]
      : [availableTradingTenors.find(tenor => tenor?.code === selectedTenor)];
    const nonUndefinedTenorsUsed = tenorsUsed.filter(tenor => (isSpread ? tenor?.left && tenor?.right : tenor !== undefined)) as ProductTenor[];

    setWidgetTenorMap(prev => ({
      ...prev,
      [id]: {
        isSpread,
        exchange: selectedExchange,
        tenorsUsed: nonUndefinedTenorsUsed,
        product: selectedProduct,
      },
    }));
  }, [id, tradingForm, availableTradingTenors]);

  if (tradingFormLoadingPanel) {
    return (
      <Stack direction="column" flex={1} alignItems="center" justifyContent="center">
        <Typography fontSize={11} color="textSecondary" tabIndex={-1}>
          Loading...
        </Typography>
      </Stack>
    );
  }
  if (!isValidExchange) {
    return (
      <Stack direction="column" flex={1} alignItems="center" justifyContent="center" spacing={1}>
        <Typography fontSize={12} color="error" textAlign="center" tabIndex={-1}>
          Invalid Exchange
        </Typography>
        <Typography fontSize={10} color="textSecondary" textAlign="center" tabIndex={-1}>
          The selected product "{tradingForm.selectedProduct}" does not support the exchange "{tradingForm.selectedExchange}"
        </Typography>
        <Typography fontSize={9} color="textSecondary" textAlign="center" tabIndex={-1}>
          Please select a different exchange or product
        </Typography>
        <TradingFormControls
          tradingForm={tradingForm}
          allowedTradingProducts={tradingFormAllowedTradingProducts}
          validTenors={validTenors}
          validBackTenors={validBackTenors}
          userTradeLimits={userTradeLimits}
          onProductChange={onTradingProductChange}
          onTenorChange={onTenorChange}
          onExchangeChange={onExchangeChange}
          onOrderTypeChange={onOrderTypeChange}
          isValidExchange={false}
        />
      </Stack>
    );
  }

  if (tradeConfirmationVisible) {
    return (
      <TradeConfirmation
        otcSide={confirmationSide!}
        tradingForm={tradingForm}
        availableTradingTenors={availableTradingTenors}
        prices={prices}
        onClose={onCloseTradeConfirmation}
        onConfirm={() => onPlaceTrade(confirmationSide!)}
        lockedBuyPrice={tradingForm.lockedBuyPrice}
        lockedSellPrice={tradingForm.lockedSellPrice}
        orderType={tradingForm.selectedOrderType}
        limitBuyPrice={limitBuyPrice}
        limitSellPrice={limitSellPrice}
      />
    );
  }
  return (
    <Stack
      display="flex"
      flexDirection="column"
      flex={1}
      alignItems="center"
      padding={1}
      justifyContent="space-between"
      onMouseEnter={handleSettlementTimeCheck}
      onFocus={handleSettlementTimeCheck}
      onKeyDown={handleSettlementTimeCheck}
      onMouseLeave={handleSettlementTimeCheck}
    >
      <TradingFormControls
        tradingForm={tradingForm}
        allowedTradingProducts={tradingFormAllowedTradingProducts}
        validTenors={validTenors}
        validBackTenors={validBackTenors}
        userTradeLimits={userTradeLimits}
        onProductChange={onTradingProductChange}
        onTenorChange={onTenorChange}
        onExchangeChange={onExchangeChange}
        onOrderTypeChange={onOrderTypeChange}
        isValidExchange={isValidExchange}
      />

      <TradeSizeInput
        sizeInputRef={sizeInputRef}
        tradeSize={tradingForm.tradeSize}
        isTradeLimitBreached={tradingFormTradeLimitBreached}
        showUserInputError={showUserInputError}
        onTradeSizeChange={onTradeSizeChange}
        rawTradingAmount={rawTradingAmount}
        isBelowMinTrade={isBelowMinTrade}
        minTrade={minTrade}
        handleTradeSizeUpdating={handleTradeSizeUpdating}
        orderType={tradingForm.selectedOrderType}
      />

      {tradingForm.selectedOrderType === OtcOrderType.limit && (
        <>
          <TradingPanelPriceEntry
            setBuyPrice={(v: number) => {
              setLimitBuyPrice(v);
            }}
            setSellPrice={(v: number) => {
              setLimitSellPrice(v);
            }}
            tradeState={limitTradeState}
            setTradeState={setLimitTradeState}
            hasError={showUserInputError}
            tickSize={tickSize}
          />
        </>
      )}

      {subscriptionSymbol && subscriptionSize ? (
        <TradingButtons
          rfqSubscriptionSymbol={subscriptionSymbol}
          rfqSubscriptionSize={subscriptionSize}
          rfSubscriptionExchange={subscriptionExchange}
          isDisabled={disableTradeButtons}
          tradingAccount={payload.selectedTradingAccount}
          tradeSize={tradingForm.tradeSize}
          isLockedBuy={tradingForm.isLockedBuy}
          isLockedSell={tradingForm.isLockedSell}
          lockedBuyPrice={tradingForm.lockedBuyPrice}
          lockedSellPrice={tradingForm.lockedSellPrice}
          onToggleBuyLock={onToggleBuyLock}
          onToggleSellLock={onToggleSellLock}
          isBelowMinTrade={isBelowMinTrade}
          isQuantityUnitSpecified={isQuantityUnitSpecified}
          showTradeConfirmation={showTradeConfirmation}
          onPlaceTrade={onPlaceTrade}
          orderType={tradingForm.selectedOrderType}
          limitTradeState={limitTradeState}
          limitBuyPrice={limitBuyPrice}
          limitSellPrice={limitSellPrice}
          isMocAfterSettlementTime={isMocAfterSettlementTime}
        />
      ) : (
        <CircularProgress size={10} />
      )}
    </Stack>
  );
};
